import type { BoxProps, IconSource, TextProps } from '@shopify/polaris';

import { Box, InlineGrid, Icon, Text, InlineStack } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { useCallback, useMemo } from 'react';
import { GClickable } from '../GClickable';
import { cls } from '~/utils';

interface GActivatorPopoverProps {
  onClick: () => void;
  active: boolean;
  content: string;
  /**
   * @param tone
   *  - @default: `base` when inactive, `subdued` when active
   *  - Overrides both states when provided
   * @param variant
   *  - @default: `bodyMd`
   */
  textProps?: Pick<TextProps, 'tone' | 'variant' | 'truncate'>;
  /**
   * @param disabled
   *  - @default: `false`
   */
  boxProps?: BoxProps;
  icon?: IconSource;
  disabled?: boolean;
}

export const GActivatorPopover = ({
  onClick,
  active,
  content,
  textProps,
  boxProps: customBoxProps,
  disabled,
  icon: customIcon,
}: GActivatorPopoverProps) => {
  const boxProps = useMemo<BoxProps>(() => {
    if (disabled) {
      return {
        borderColor: 'border-disabled',
        background: 'bg-surface-disabled',
        ...customBoxProps,
      };
    }

    return {
      shadow: active ? 'button-inset' : 'button',
      background: active ? 'bg-fill-active' : 'bg-fill',
      ...customBoxProps,
    };
  }, [active, disabled, customBoxProps]);

  const toneClass = useMemo<string>(() => {
    if (disabled) {
      return 'text-[var(--p-color-text-disabled)]';
    }

    return active ? 'text-[var(--p-color-text-subdued)]' : 'text-[var(--p-color-text)]';
  }, [active, disabled]);

  const textComponent = useMemo(() => {
    return (
      <Text
        as="span"
        variant="bodyMd"
        fontWeight="medium"
        tone={disabled ? 'inherit' : textProps?.tone}
        truncate
        {...(textProps?.variant && { variant: textProps.variant })}
      >
        {content}
      </Text>
    );
  }, [content, textProps, disabled]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    onClick();
  }, [disabled, onClick]);

  return (
    <GClickable cursor={disabled ? 'default' : 'pointer'} onClick={handleClick}>
      <div className={cls(toneClass)}>
        <Box
          padding="150"
          paddingInline="300"
          paddingInlineEnd="150"
          borderRadius="200"
          {...boxProps}
        >
          {customIcon && (
            <InlineStack gap="200" align="center" wrap={false}>
              <Box>
                <Icon source={customIcon} />
              </Box>
              {textComponent}
            </InlineStack>
          )}

          {!customIcon && (
            <InlineGrid columns="1fr 20px" gap="200" alignItems="center">
              {textComponent}
              <Icon source={active ? ChevronUpIcon : ChevronDownIcon} />
            </InlineGrid>
          )}
        </Box>
      </div>
    </GClickable>
  );
};
